import { useMemo } from "react";
import { ApiPermissions, ConnectionStatus, PublicAccount, userFacingMessages } from "@cede/types";
import { getExchangeLogo } from "@cede/utils";
import { UseAccountsHook } from "./types";
import { useHubUser } from "@/hooks/user/useHubUser";

export const useAccounts: UseAccountsHook = () => {
  const { hubUser, isLoadingHubUser } = useHubUser();

  const { accounts, droplistAccounts, accountsByIds, accountsIds, areAccountsLoading } = useMemo(() => {
    if (!hubUser)
      return {
        accounts: [],
        droplistAccounts: [],
        accountsByIds: {},
        accountsIds: [],
        areAccountsLoading: isLoadingHubUser,
      };

    const mapStatus = (status?: string): ConnectionStatus => {
      if (status === ConnectionStatus.RECONNECTION_REQUIRED) return ConnectionStatus.RECONNECTION_REQUIRED;
      if (status === ConnectionStatus.OFFLINE) return ConnectionStatus.OFFLINE;
      return ConnectionStatus.ONLINE;
    };

    const publicAccounts: PublicAccount[] =
      hubUser.exchangeAccounts?.map((account) => ({
        id: account.id.toString(),
        status: mapStatus(account.status),
        accountName: account.name,
        exchangeId: account.exchangeId,
        permissions: [ApiPermissions.READ, ApiPermissions.TRADE],
        exchangeUserId: account.accountId ?? undefined,
      })) ?? [];

    const mappedAccounts = publicAccounts.map((account) => ({
      label: account.accountName,
      value: account.id,
      img: getExchangeLogo(account.exchangeId),
      cexValue: account.exchangeId,
      exchangeUserId: account.exchangeUserId,
      permissions: account.permissions,
      disabledText:
        account.status === ConnectionStatus.RECONNECTION_REQUIRED
          ? userFacingMessages.ACCOUNT.RECONNECTION_REQUIRED
          : undefined,
      isDisabled: [ConnectionStatus.OFFLINE, ConnectionStatus.RECONNECTION_REQUIRED].includes(account.status),
    }));

    const mappedAccountsById = publicAccounts.reduce(
      (acc, account) => {
        acc[account.id] = account;
        return acc;
      },
      {} as Record<string, PublicAccount>,
    );

    return {
      accounts: publicAccounts,
      droplistAccounts: mappedAccounts,
      accountsByIds: mappedAccountsById,
      accountsIds: publicAccounts.map((account) => account.id),
      areAccountsLoading: isLoadingHubUser,
    };
  }, [hubUser, isLoadingHubUser]);

  return {
    accounts,
    droplistAccounts,
    accountsByIds,
    accountsIds,
    areAccountsLoading,
  };
};
